/**
 * Final composite. The only pass that runs at output resolution and the only one that
 * writes to the default framebuffer. Everything it needs arrives as a uniform, including
 * the derived mask fade, so the whole look retunes without a shader recompile.
 */

import * as THREE from 'three';
import { LINEAR, tokenIndex } from '../palette';
import { CRT_COMPOSITE_FRAG } from '../shaders/crt';
import { FullscreenPass } from './FullscreenPass';

/** AMD-3: default 0.18, hard cap 0.35 — beyond that the telegraph-edge contrast fails. */
export const SCANLINE_DEFAULT = 0.18;
export const SCANLINE_MAX = 0.35;
/**
 * AMD-6 D12 readability cap, stricter than the AMD-3 slider ceiling above.
 *
 * A grille at 0.18 halves vertical resolution: every other buffer row is dimmed, and a
 * 1-unit-tall enemy at 36 px loses 18 of them. The slider keeps its AMD-3 range for
 * anyone who wants the artefact; the applied value is clamped here.
 */
export const SCANLINE_READABILITY_CAP = 0.1;
/** AMD-6 D12: below this output height the barrel distortion is off entirely. */
export const BARREL_MIN_HEIGHT = 900;

/**
 * Device pixels per scanline period. Three is the smallest pitch that still reads as
 * structure rather than as a grey wash once the grille is composited on top; measured in
 * output pixels, so it is the same artefact at every resolution and on every DPR.
 */
export const SCANLINE_PITCH_PX = 3;
/** AMD-6 D12: grain ceiling. Above this the black floor lifts measurably. */
export const GRAIN_MAX = 0.02;
/** AMD-6 D12: vignette, pulled down from 0.38 and flattened. */
export const VIGNETTE_DEFAULT = 0.28;

/** Lomo edge softness, in output pixels at the extreme corner. 0 disables the taps. */
export const EDGE_BLUR_DEFAULT = 2.2;

export class CrtComposite {
  private readonly pass: FullscreenPass;
  readonly uniforms: Record<string, THREE.IUniform>;
  private outputHeight = 1080;
  private scanlineRequest = SCANLINE_DEFAULT;

  /**
   * AMD-6 D12 — the applied grille strength: the player's setting, clamped to the
   * readability cap, and off outright below a 900 px viewport where a scanline period
   * lands on top of a source pixel and turns into moiré rather than structure.
   */
  private applyScanline(): void {
    const wanted = Math.max(0, Math.min(SCANLINE_MAX, this.scanlineRequest));
    // The height gate is gone with the buffer-space period it was guarding (see the
    // shader): a pitch fixed in device pixels cannot beat against the source grid, so a
    // 390 px phone gets the same faint structure a 1440p window does.
    this.uniforms.uScanlineStrength.value = Math.min(wanted, SCANLINE_READABILITY_CAP);
  }

  /** The player's slider value, before the readability cap. */
  setScanlineRequest(v: number): void {
    this.scanlineRequest = v;
    this.applyScanline();
  }

  setVignette(v: number): void {
    this.uniforms.uVignette.value = Math.max(0, Math.min(1, v));
  }

  /** Output pixels of corner softness. The governor zeroes this on the lower tiers. */
  setEdgeBlur(px: number): void {
    this.uniforms.uEdgeBlur.value = Math.max(0, px);
  }

  constructor() {
    this.uniforms = {
      tScene: { value: null },
      tPersist: { value: null },
      tBloom: { value: null },
      tHalation: { value: null },

      uOutputSize: { value: new THREE.Vector2(1, 1) },
      uBufferSize: { value: new THREE.Vector2(1, 1) },
      uTime: { value: 0 },

      uPersistMix: { value: 1 },
      uBloomIntensity: { value: 0.85 },
      uHalationStrength: { value: 0.3 },
      uHalationTint: { value: new THREE.Vector3(1.0, 0.72, 0.55) },
      uExposure: { value: 1.15 },

      uCurvature: { value: new THREE.Vector2(0.018, 0.026) },
      uOverscan: { value: 1.006 },
      uChromaticAberration: { value: 0.6 },

      uMaskPitch: { value: 3.0 },
      uMaskStrength: { value: 0.3 },
      uScanlineStrength: { value: SCANLINE_DEFAULT },
      uScanlineSharpness: { value: 2.2 },
      uScanlinePitch: { value: SCANLINE_PITCH_PX },
      uEdgeBlur: { value: EDGE_BLUR_DEFAULT },
      uMaskFade: { value: 1 },

      uVignette: { value: VIGNETTE_DEFAULT },
      uNoise: { value: 0.014 },

      uFlash: { value: new THREE.Vector4(1, 1, 1, 0) },
      uLetterbox: { value: 0 },
      uSlowmo: { value: 0 },
      uPlayerRim: { value: new THREE.Vector3(-10, -10, 0) },
      uPlayerRimColor: { value: new THREE.Vector3(1, 1, 1) },
    };
    const core = tokenIndex('pal.ui.core') * 3;
    (this.uniforms.uPlayerRimColor.value as THREE.Vector3).set(LINEAR[core], LINEAR[core + 1], LINEAR[core + 2]);

    this.pass = new FullscreenPass(CRT_COMPOSITE_FRAG, this.uniforms);
  }

  /** AMD-6 D16 — bullet-time strength, 0..1. */
  setSlowmo(v: number): void {
    this.uniforms.uSlowmo.value = Math.max(0, Math.min(1, v));
  }

  /** AMD-6 D5 — the post-bloom player rim, in buffer uv with an aspect-corrected radius. */
  setPlayerRim(u: number, v: number, radius: number): void {
    (this.uniforms.uPlayerRim.value as THREE.Vector3).set(u, v, Math.max(0, radius));
  }

  setTextures(scene: THREE.Texture, persist: THREE.Texture, bloom: THREE.Texture, halation: THREE.Texture): void {
    this.uniforms.tScene.value = scene;
    this.uniforms.tPersist.value = persist;
    this.uniforms.tBloom.value = bloom;
    this.uniforms.tHalation.value = halation;
  }

  /**
   * `scale` is output pixels per buffer pixel. Below roughly two, a three-pixel grille
   * and a one-pixel-per-row scanline land on top of each other and turn into moiré, so
   * both fade out rather than fighting the sampler.
   */
  setGeometry(outputW: number, outputH: number, bufferW: number, bufferH: number, scale: number): void {
    (this.uniforms.uOutputSize.value as THREE.Vector2).set(outputW, outputH);
    (this.uniforms.uBufferSize.value as THREE.Vector2).set(bufferW, bufferH);
    this.uniforms.uMaskFade.value = THREE.MathUtils.smoothstep(scale, 1.0, 2.5);
    this.outputHeight = outputH;
    this.applyScanline();
    // AMD-6 D12: barrel distortion is off below the desktop framing entirely. A bowed
    // frame on a 6-inch screen costs the corners and buys nothing.
    const bow = outputH >= BARREL_MIN_HEIGHT ? 1 : 0;
    (this.uniforms.uCurvature.value as THREE.Vector2).set(0.018 * bow, 0.026 * bow);
    this.uniforms.uOverscan.value = bow === 1 ? 1.006 : 1;
  }

  set time(value: number) {
    this.uniforms.uTime.value = value;
  }

  /** §11.8 — `strength` is the displayed-luminance contribution; the shader clamps 0.35. */
  setFlash(r: number, g: number, b: number, strength: number): void {
    (this.uniforms.uFlash.value as THREE.Vector4).set(r, g, b, strength);
  }

  set letterbox(value: number) {
    this.uniforms.uLetterbox.value = Math.max(0, Math.min(0.25, value));
  }

  render(renderer: THREE.WebGLRenderer): void {
    this.pass.render(renderer, null);
  }

  dispose(): void {
    this.pass.dispose();
  }
}
